import React, { useMemo, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

import { logOut } from "../services/authService";
import { useAuth } from "../context/AuthContext";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/config";
import { NavHomeIcon, NavRecoveryIcon, NavJournalIcon, NavProfileIcon, NavRedFlagsIcon, NavGPSummaryIcon } from "./Icons";
import { useWeekContext } from "../context/WeekContext";

interface AppShellHeaderConfig {
  title: string;
  subtitle?: string;
  label?: string;
  gradient?: string;
  height?: number | string;
  className?: string;
  contentClassName?: string;
  headerActions?: React.ReactNode;
  headerExtra?: React.ReactNode;
}

interface AppShellProps {
  title?: string;
  children: React.ReactNode;
  banner?: React.ReactNode;
  header?: React.ReactNode;
  headerConfig?: AppShellHeaderConfig;
  contentTitle?: React.ReactNode;
  variant?: "home" | "default";
  /** When true, only show Profile + Logout in the bottom nav */
  minimalNav?: boolean;
}

const cx = (...values: Array<string | false | null | undefined>) =>
  values.filter(Boolean).join(" ");

const PAGE_COLORS: Record<string, string> = {
  "/dashboard":  "#B19ABA",
  "/recovery":   "#C6D8A5",
  "/journal":    "#C5A499",
  "/red-flags":  "#7D2248",
  "/gp-summary": "#6C7A52",
  "/profile":    "#6C7A52",
};

const getNavColor = (pathname: string): string =>
  Object.entries(PAGE_COLORS).find(
    ([route]) => pathname === route || pathname.startsWith(route + "/")
  )?.[1] ?? "#B19ABA";

const navItemClass = (active: boolean, hideLabels?: boolean) => {
  void active;
  return `relative flex h-14 min-w-0 flex-1 flex-col items-center justify-center ${hideLabels ? "gap-0 sm:gap-1.5" : "gap-1.5"} no-underline`;
};

interface BottomNavProps {
  isActive: (href: string) => boolean;
  onLogout: () => void;
  minimal?: boolean;
  currentPath: string;
  gpSummaryUnlocked?: boolean;
}

const BottomNav: React.FC<BottomNavProps> = ({ isActive, onLogout, minimal, currentPath, gpSummaryUnlocked }) => (
  <nav
    style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      height: '72px',
      zIndex: 100,
      backgroundColor: '#ffffff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      paddingLeft: '8px',
      paddingRight: '8px',
      transform: 'translateZ(0)',
      WebkitTransform: 'translateZ(0)',
      backfaceVisibility: 'hidden',
      WebkitBackfaceVisibility: 'hidden' as React.CSSProperties['WebkitBackfaceVisibility'],
      willChange: 'transform',
      borderTop: '1px solid #F0F0F0',
    }}
  >
    <ul className={cx("m-0 flex w-full list-none items-center gap-0 p-0", minimal ? "justify-center" : "justify-between")}>
      {!minimal && (
        <>
          <li>
            <Link to="/dashboard" className={navItemClass(isActive("/dashboard"), true)}>
              {isActive("/dashboard") && (
                <span className="absolute -top-1.5 left-0 right-0 h-1.5 rounded-b-[30px]" style={{ backgroundColor: getNavColor(currentPath) }} />
              )}
              <NavHomeIcon />
              <span className="hidden text-[10px] font-medium leading-4 sm:inline" style={{ color: isActive("/dashboard") ? getNavColor(currentPath) : "#1E5E5B" }}>Home</span>
            </Link>
          </li>
          <li>
            <Link to="/recovery" className={navItemClass(isActive("/recovery"), true)}>
              {isActive("/recovery") && (
                <span className="absolute -top-1.5 left-0 right-0 h-1.5 rounded-b-[30px]" style={{ backgroundColor: getNavColor(currentPath) }} />
              )}
              <NavRecoveryIcon />
              <span className="hidden text-[10px] font-medium leading-4 sm:inline" style={{ color: isActive("/recovery") ? getNavColor(currentPath) : "#1E5E5B" }}>Recovery</span>
            </Link>
          </li>
          <li>
            <Link to="/journal" className={navItemClass(isActive("/journal"), true)}>
              {isActive("/journal") && (
                <span className="absolute -top-1.5 left-0 right-0 h-1.5 rounded-b-[30px]" style={{ backgroundColor: getNavColor(currentPath) }} />
              )}
              <NavJournalIcon />
              <span className="hidden text-[10px] font-medium leading-4 sm:inline" style={{ color: isActive("/journal") ? getNavColor(currentPath) : "#1E5E5B" }}>Journal</span>
            </Link>
          </li>
          <li>
            <Link to="/red-flags" className={navItemClass(isActive("/red-flags"), true)}>
              {isActive("/red-flags") && (
                <span
                  className="absolute -top-1.5 left-0 right-0 h-1.5 rounded-b-[30px]"
                  style={{ backgroundColor: getNavColor(currentPath) }}
                />
              )}
              <div style={{ color: isActive("/red-flags") ? getNavColor(currentPath) : "#1E5E5B" }}>
                <NavRedFlagsIcon />
              </div>
              <span
                className="hidden text-[10px] font-medium leading-4 sm:inline"
                style={{ color: isActive("/red-flags") ? getNavColor(currentPath) : "#1E5E5B" }}
              >
                Red Flags
              </span>
            </Link>
          </li>
          <li>
            {gpSummaryUnlocked ? (
              <Link to="/gp-summary" className={navItemClass(isActive("/gp-summary"), true)}>
                {isActive("/gp-summary") && (
                  <span className="absolute -top-1.5 left-0 right-0 h-1.5 rounded-b-[30px]" style={{ backgroundColor: "#6C7A52" }} />
                )}
                <NavGPSummaryIcon />
                <span className="hidden text-[10px] font-medium leading-4 sm:inline" style={{ color: isActive("/gp-summary") ? "#6C7A52" : "#1E5E5B" }}>Summary</span>
              </Link>
            ) : (
              <span className={navItemClass(false, true)} style={{ opacity: 0.4, cursor: "not-allowed" }}>
                <NavGPSummaryIcon locked />
                <span className="hidden text-[10px] font-medium leading-4 text-gray-400 sm:inline">Summary</span>
              </span>
            )}
          </li>
        </>
      )}
      <li className={minimal ? "flex" : "hidden md:flex"}>
        <button onClick={onLogout} className={navItemClass(false)}>
          <img
            src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAACXBIWXMAAAsTAAALEwEAmpwYAAAB00lEQVR4nO2ZS0oEQQyGC8YbKILSlRLxACK49H2GRkw6GW/gQsSdS3Etvnaey4XgY6GI4gMcEQaUbkdnFEXFpLWlPih6V50/lVSHv52LRCKRP03f3FwPMG2B4DEINUHoQWE1gekIGHcGEINZ8EFwygudKgX97vKMN5Bl0yaZtw4enhfT2WCW9aoKAKHtlywJHuan4dK0prJ5mtYSwXEvdNAWgptOk6JGW5sXwRsAdZppJ4kOdDfvaFi1zL8lTWte6L71nobq3p01qrrx2/cwrXrGu/xZSQFmRAFfJMmyocC4mD9dRXtgv/UtuACikeoKEGURpQkgGikC1xZRZhOHOg6D4HnHfHSZzNNopW6hoC3iN67RoCniMwH5tQdMSyC4rLl8huvAdPvjnvhMwKvbw37c3o8CPiihFWBaU167xXBnXUKVb2JtQpWv0WDyIWsPWd9voL8wSiRWY25Zw1xZgNU4XRaJ1Ul7oQUQ3MufroqAUMPE7igL+Ae2StPc2JqY6LIztjqsRU805gwIdZq0tBZfmbu5j6lp7ubBe0tzN9RnBwrvvoRx2TNdDTKD0ybPumc8MQ1e8Lo4XSv6mbu90EZRRoq/mJ7KBzdNMh+JRCKuLB4BRuMQhr9oCtkAAAAASUVORK5CYII="
            alt="Log out"
            className="h-6 w-6"
          />
          <span className="text-[10px] font-medium leading-4 text-[#1E5E5B]">Logout</span>
        </button>
      </li>
      <li>
        <Link to="/profile" className={navItemClass(isActive("/profile"), !minimal)}>
          {isActive("/profile") && (
            <span className="absolute -top-1.5 left-0 right-0 h-1.5 rounded-b-[30px]" style={{ backgroundColor: getNavColor(currentPath) }} />
          )}
          <NavProfileIcon />
          <span className={cx("text-[10px] font-medium leading-4", !minimal && "hidden sm:inline")} style={{ color: isActive("/profile") ? getNavColor(currentPath) : "#1E5E5B" }}>Profile</span>
        </Link>
      </li>
    </ul>
  </nav>
);

const AppShell: React.FC<AppShellProps> = ({
  children, banner, header, headerConfig, contentTitle, variant, minimalNav,
}) => {
  const location = useLocation();
  const { user, ftuCompleted } = useAuth();
  const { unlockedWeek } = useWeekContext();

  const handleLogout = async () => {
    try {
      await logOut();
    } catch (err) {
      console.error("Error logging out:", err);
    }
  };

  useEffect(() => {
    if (!user?.uid || !ftuCompleted) return;

    const uid = user.uid;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        updateDoc(doc(db, "users", uid), {
          hasVisitedDashboard: true,
          updatedAt: serverTimestamp(),
        }).catch(() => {});
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [user?.uid, ftuCompleted]);

  const isActive = useMemo(
    () => (href: string) =>
      location.pathname === href || location.pathname.startsWith(href + "/"),
    [location.pathname]
  );

  const renderHeaderContent = () => {
    if (!headerConfig) return null;
    return (
      <div className={cx("relative px-6 pt-[30px]", headerConfig.contentClassName)}>
        {headerConfig.headerActions && (
          <div className="absolute top-0 right-0 pt-[30px] pr-6 flex gap-2">
            {headerConfig.headerActions}
          </div>
        )}
        <h1 className="m-0 font-dm-sans text-[32px] font-semibold leading-[42px] text-white">
          {headerConfig.title}
        </h1>
        {headerConfig.subtitle && (
          <p
            className="m-0 mt-1"
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontStyle: "normal",
              fontWeight: 500,
              fontSize: "20px",
              lineHeight: "26px",
              color: "#FFFFFF",
              flex: "none",
              order: 1,
              alignSelf: "stretch",
              flexGrow: 0,
            }}
          >
            {headerConfig.subtitle}
          </p>
        )}
        {headerConfig.headerExtra}
      </div>
    );
  };

  const getHeaderHeightValue = (): string | undefined => {
    if (!headerConfig) return undefined;
    if (typeof headerConfig.height === "number") return `${headerConfig.height}px`;
    return headerConfig.height ?? "224px";
  };

  const homeContentTop = useMemo(() => {
    if (variant !== "home" || !headerConfig?.height) return 175;
    const h = typeof headerConfig.height === "number"
      ? headerConfig.height
      : parseInt(String(headerConfig.height), 10);
    return isNaN(h) ? 175 : h - 35;
  }, [variant, headerConfig?.height]);

  const renderHeader = () => {
    if (headerConfig) {
      return (
        <header
          className={cx("absolute left-0 top-0 w-full", headerConfig.className)}
          style={{
            backgroundImage:
              headerConfig.gradient ??
              "linear-gradient(167.14deg, #000000 -2.38%, #EDE4F1 94.45%)",
            minHeight:
              typeof headerConfig.height === "number"
                ? `${headerConfig.height}px`
                : (headerConfig.height ?? "224px"),
          }}
        >
          <div className="relative z-[1]">{renderHeaderContent()}</div>
        </header>
      );
    }
    return header ?? null;
  };

  return (
    <div
      className={cx(
        "relative w-full",
        variant === "home" && "bg-[linear-gradient(167.14deg,_#B19ABA_-2.38%,_#EDE4F1_94.45%)]"
      )}
    >
      {renderHeader()}

      <div className="w-full">
        {banner}
        <main className="w-full">
          <div
            className={cx(
              "w-full box-border",
              variant === "home" &&
                "absolute left-0 right-0 flex flex-col items-start gap-6 overflow-x-visible rounded-t-[32px] bg-[#FCF9F7] px-0 pb-[72px] pt-6"
            )}
            style={
              variant === "home"
                ? { top: `${homeContentTop}px` }
                : {
                    paddingTop: `calc(${getHeaderHeightValue()} - 30px)`,
                    paddingBottom: "72px",
                  }
            }
          >
            {contentTitle}
            {children}
          </div>
        </main>
      </div>

      <BottomNav isActive={isActive} onLogout={handleLogout} minimal={minimalNav} currentPath={location.pathname} gpSummaryUnlocked={unlockedWeek >= 6} />
    </div>
  );
};

export default AppShell;